import { Receipt } from 'lucide-react'
import { Link } from 'react-router-dom'

import { CompactInvoiceCard } from '@/customer/components/billing/invoice-card'
import { InvoiceList } from '@/customer/components/billing/invoice-list'
import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { PageContainer } from '@/customer/components/page-container'
import type { StackedHeadlineItem } from '@/customer/components/stacked-headline'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { useGetMe } from '@/customer/hooks/use-get-me'
import { useListInvoices } from '@/customer/hooks/use-list-invoices'
import { resolvePetNames } from '@/customer/lib/pets'
import { routes } from '@/customer/routes'

const HEADLINE: StackedHeadlineItem[] = [
  { text: 'Your', color: 'yellow' },
  { text: 'payments', color: 'green' },
]

export function BillingPage() {
  useDocumentTitle('Billing')
  const me = useGetMe()
  const { invoices, isPending } = useListInvoices()

  const petNamesById = new Map(me.data?.patients.map((p) => [p.id, p.name]) ?? [])
  const overdueInvoices = invoices.filter((inv) => inv.overdue)
  const otherInvoices = invoices.filter((inv) => !inv.overdue)

  if (isPending || me.isPending)
    return (
      <PageContainer headline={HEADLINE}>
        <LoadingSpinner />
      </PageContainer>
    )

  return (
    <PageContainer headline={HEADLINE}>
      <div className="flex flex-col gap-10">
        {overdueInvoices.length > 0 && (
          <section className="flex flex-col gap-4" aria-labelledby="overdue-invoices-heading">
            <div className="space-y-2">
              <h2 id="overdue-invoices-heading" className="text-headline-sm">
                Overdue Invoices
              </h2>
              <p className="text-body-md">
                These invoices need your attention. Select an invoice to view the details and make a
                payment.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {overdueInvoices.map((inv) => (
                <CompactInvoiceCard
                  key={inv.id}
                  invoice={inv}
                  petName={resolvePetNames(inv.petIds, petNamesById)}
                />
              ))}
            </div>
          </section>
        )}

        <section className="flex flex-col gap-4" aria-labelledby="payment-methods-heading">
          <div className="space-y-2">
            <h2 id="payment-methods-heading" className="text-headline-sm">
              Payment Methods
            </h2>
            <p className="text-body-md">
              Manage your membership payment methods on the{' '}
              <Link to={routes.account.pets.index} className="underline underline-offset-4">
                Pets page
              </Link>
              .
            </p>
          </div>
        </section>

        <section className="flex flex-col gap-4" aria-labelledby="invoice-history-heading">
          <h2 id="invoice-history-heading" className="text-headline-sm">
            Invoice History
          </h2>
          {otherInvoices.length > 0 ? (
            <InvoiceList invoices={otherInvoices} petNamesById={petNamesById} />
          ) : (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed py-12">
              <Receipt className="size-8 text-muted-foreground" aria-hidden />
              <p className="text-label-md text-muted-foreground">
                {invoices.length === 0 ? 'No invoices yet.' : 'No other invoices to show.'}
              </p>
            </div>
          )}
        </section>
      </div>
    </PageContainer>
  )
}
